"use client";

import React from "react";
import Link from "next/link";
import { 
  Building2, 
  MapPin, 
  Search, 
  Calendar, 
  KanbanSquare, 
  Sparkles, 
  Shield, 
  Award,
  TrendingUp
} from "lucide-react";

interface RealEstateHeroProps {
  cityFilter: string;
  setCityFilter: (c: string) => void;
  typeFilter: string;
  setTypeFilter: (t: string) => void;
  priceFilter: string;
  setPriceFilter: (p: string) => void;
  onOpenSiteVisit: () => void;
  currency: "INR" | "USD";
}

export default function RealEstateHero({
  cityFilter,
  setCityFilter,
  typeFilter,
  setTypeFilter,
  priceFilter,
  setPriceFilter,
  onOpenSiteVisit,
  currency,
}: RealEstateHeroProps) {
  return (
    <div className="relative overflow-hidden bg-slate-950 text-white pt-12 pb-16 border-b border-slate-800">
      {/* Background Glows & Mesh */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 right-10 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Brand Kicker */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold tracking-wider uppercase shadow-inner">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span>Aura Prime Estates • Private Wealth & Real Estate Portal</span>
          </div>

          <div className="flex items-center gap-3">
            <Link
              href="/demos/real-estate/crm"
              className="px-3.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-amber-500/30 text-amber-400 text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm group"
            >
              <KanbanSquare className="w-3.5 h-3.5" />
              <span>Explore Real Estate Sales CRM</span>
              <span className="text-[10px] text-slate-400 group-hover:text-white transition-colors">→</span>
            </Link>
          </div>
        </div>

        {/* Main Headline */}
        <div className="max-w-3xl mb-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-heading text-white tracking-tight leading-[1.15]">
            Curated Signature Living &amp;{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600">
              High-Yield Assets.
            </span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed font-normal">
            Direct developer allocations, private duplex penthouses, and pre-leased commercial towers across prime corridors. Experience end-to-end site inspection concierge and digital deed execution.
          </p>
        </div>

        {/* Interactive Search Console */}
        <div className="p-4 sm:p-5 rounded-2xl bg-slate-900/90 border border-slate-800 shadow-2xl backdrop-blur-xl max-w-5xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
            {/* City Selector */}
            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-amber-400" />
                Prime Corridor
              </label>
              <select
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-medium focus:border-amber-500 focus:outline-none"
              >
                <option value="ALL">All Global Cities</option>
                <option value="Mumbai">Mumbai (Worli / Bandra)</option>
                <option value="Bangalore">Bangalore (Whitefield / Indiranagar)</option>
                <option value="Dubai">Dubai (DIFC / Palm Jumeirah)</option>
                <option value="London">London (Kensington / Mayfair)</option>
                <option value="Goa">North Goa (Coastal Estates)</option>
                <option value="Chennai">Chennai (ECR Seafront)</option>
              </select>
            </div>

            {/* Property Type */}
            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Building2 className="w-3 h-3 text-amber-400" />
                Asset Classification
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-medium focus:border-amber-500 focus:outline-none"
              >
                <option value="ALL">All Asset Classes</option>
                <option value="Penthouse">Sky Penthouses</option>
                <option value="Villa">Ultra-Luxury Villas</option>
                <option value="Commercial">Commercial Floors (Pre-Leased)</option>
                <option value="Apartment">Luxury Seafront Apartments</option>
              </select>
            </div>

            {/* Budget Filter */}
            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-amber-400" />
                Investment Bracket
              </label>
              <select
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-medium focus:border-amber-500 focus:outline-none"
              >
                <option value="ALL">All Price Tiers</option>
                {currency === "INR" ? (
                  <>
                    <option value="under5">Under ₹5.00 Cr</option>
                    <option value="5to10">₹5.00 Cr – ₹10.00 Cr</option>
                    <option value="above10">Above ₹10.00 Cr</option>
                  </>
                ) : (
                  <>
                    <option value="under5">Under $600,000</option>
                    <option value="5to10">$600,000 – $1.5M</option>
                    <option value="above10">Above $1.5M</option>
                  </>
                )}
              </select>
            </div>

            {/* Action CTA */}
            <div className="flex items-end">
              <button
                type="button"
                onClick={onOpenSiteVisit}
                className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-slate-950 font-bold text-xs shadow-lg hover:shadow-amber-500/20 transition-all hover:scale-[1.02] flex items-center justify-center gap-2 font-heading"
              >
                <Calendar className="w-3.5 h-3.5" />
                <span>Book VIP Site Visit</span>
              </button>
            </div>
          </div>
        </div>

        {/* Trust Badges Bar */}
        <div className="mt-8 pt-6 border-t border-slate-800/80 grid grid-cols-2 md:grid-cols-4 gap-4 text-slate-400 text-xs">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-amber-400 shrink-0" />
            <span>₹1,850+ Cr Assets Transacted</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>RERA Approved &amp; Title Verified</span>
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-400 shrink-0" />
            <span>Direct Developer Allocations</span>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400 shrink-0" />
            <span>Complimentary Chauffeur Viewing</span>
          </div>
        </div>
      </div>
    </div>
  );
}
